/**
 * Vehicle Realtime Collaboration Hook
 * 
 * Provides vehicle data loading, realtime updates via Appwrite subscriptions,
 * and autosave functionality.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import appwrite from '../services/appwrite';
import { dataApi } from '../services/api';
import { usePresence } from './usePresence';

const { getCurrentUser, subscribeToDocument } = appwrite;

/**
 * Hook for collaboration on a specific vehicle record
 * 
 * @param {string} vehicleId - The vehicle ID to collaborate on
 * @param {object} initialData - Initial data for the vehicle (optional)
 * @returns {object} Collaboration state and methods
 */
export function useVehicleCollaboration(vehicleId, initialData = {}) {
  const [data, setData] = useState(initialData);
  const [loading, setLoading] = useState(true);
  const [currentUser, setCurrentUser] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [isSyncing, setIsSyncing] = useState(false);
  const saveTimeoutRef = useRef(null);
  const pendingChangesRef = useRef({});
  const {
    activeUsers,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  } = usePresence(vehicleId ? `vehicle:${vehicleId}` : null);

  // Initialize current user
  useEffect(() => {
    const initUser = async () => {
      const user = await getCurrentUser();
      setCurrentUser(user);
    };
    initUser();
  }, []);

  // Load vehicle data from API
  useEffect(() => {
    if (!vehicleId) {
      setLoading(false);
      return;
    }

    const loadVehicle = async () => {
      try {
        setLoading(true);
        const vehicleData = await dataApi.vehicleGet(vehicleId);
        setData(vehicleData);
      } catch (err) {
        console.error('Error loading vehicle:', err);
      } finally {
        setLoading(false);
      }
    };

    loadVehicle();
  }, [vehicleId]);

  // Subscribe to realtime updates
  useEffect(() => {
    if (!vehicleId) return;

    const unsubscribe = subscribeToDocument('customer_vehicles', vehicleId, (response) => {
      if (response.events?.includes('databases.*.collections.*.documents.*.update')) {
        // Update local state with server changes
        setData(prev => ({
          ...prev,
          ...response.payload
        }));
      }
    });

    return () => {
      if (typeof unsubscribe === 'function') {
        unsubscribe();
      }
    };
  }, [vehicleId]);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Autosave function
  const saveChanges = useCallback(async () => {
    if (!vehicleId || Object.keys(pendingChangesRef.current).length === 0) return;

    const changesToSave = { ...pendingChangesRef.current };
    pendingChangesRef.current = {};

    try {
      setIsSyncing(true);
      await dataApi.vehicleUpdate(vehicleId, changesToSave);
    } catch (err) {
      console.error('Error saving vehicle changes:', err);
      // Re-add failed changes to pending
      pendingChangesRef.current = { ...pendingChangesRef.current, ...changesToSave };
    } finally {
      setIsSyncing(false);
    }
  }, [vehicleId]);

  // Cleanup save timeout on unmount
  useEffect(() => {
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        // Save any pending changes before unmount
        saveChanges();
      }
    };
  }, [saveChanges]);

  // Update field with debounced autosave
  const updateField = useCallback(async (fieldName, value, debounceMs = 1000) => {
    // Update local state immediately
    setData(prev => ({ ...prev, [fieldName]: value }));

    // Track pending change
    pendingChangesRef.current[fieldName] = value;

    // Debounce save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    saveTimeoutRef.current = setTimeout(saveChanges, debounceMs);
  }, [saveChanges]);

  return {
    data,
    loading,
    activeUsers,
    currentUser,
    isOnline,
    isSyncing,
    updateField,
    trackFieldEdit,
    clearFieldEdit,
    getFieldEditor,
    isCurrentUserEditing
  };
}

